import React, { useMemo } from "react";
import {
  Box,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import GlassCard from "../Cards/GlassCard";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import {
  PRIMARY,
  SUCCESS,
  WARNING,
  DANGER,
  INDIGO,
  gradientTextSx,
  dashboardTooltipSurfaceSx,
} from "../../utils/colors";

// ── Color palettes ─────────────────────────────────────────────────────────────
// const DIFF_COLORS  = [SUCCESS, WARNING, DANGER];        // Easy, Medium, Hard
const ACTIV_COLORS = [PRIMARY, INDIGO, DANGER];          // Active, Mod Active, Needs Improvement

// ── Helpers ────────────────────────────────────────────────────────────────────
const safeAvg = (arr, fn) => {
  if (!arr.length) return 0;
  const sum = arr.reduce((acc, s) => acc + (fn(s) || 0), 0);
  return Math.round(sum / arr.length);
};

// ── Summary Card ───────────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, color }) => (
  <GlassCard sx={{ borderRadius: 4, height: "100%" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 3,
          bgcolor: `${color}1a`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: '1.75rem',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1.15,
            color: 'text.primary',
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          mt={0.75}
          sx={{ fontSize: '0.8125rem', lineHeight: 1.45 }}
        >
          {label}
        </Typography>
      </Box>
    </CardContent>
  </GlassCard>
);

// ── Chart Card ─────────────────────────────────────────────────────────────────
const ChartCard = ({ title, children }) => (
  <GlassCard sx={{ borderRadius: 4, height: "100%" }}>
    <CardContent sx={{ p: 2.5, height: "100%", "&:last-child": { pb: 2.5 } }}>
      <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ letterSpacing: '-0.01em' }}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </GlassCard>
);

// ── Custom Tooltip ─────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        ...dashboardTooltipSurfaceSx,
        borderRadius: 2,
        p: 1.5,
      }}
    >
      {label && (
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          {label}
        </Typography>
      )}
      {payload.map((entry, idx) => (
        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: entry.fill || entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" fontWeight={600}>
            {entry.name || entry.dataKey}: {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const BatchAnalyticsDashboard = ({ students = [] }) => {
  const analytics = useMemo(() => {
    const total = students.length;

    // ✅ problemsSolved — from assignment_submissions via /student-stats
    // ❌ NOT: totalSolved, totalQuestion, or any LeetCode global stats field
    const avgSolved = safeAvg(students, (s) => s.problemsSolved ?? 0);

    // ✅ completionRate — from assignment_submissions via /student-stats (0–100 integer)
    // ❌ NOT: contestRating, ranking, or any LeetCode profile field
    const avgCompletion = safeAvg(students, (s) => s.completionRate ?? 0);

    // ✅ activityClassification — computed server-side from assignment submission dates
    // ❌ NOT: streak, activeDays, or any LeetCode activity field
    const active    = students.filter((s) => s.activityClassification === 'High Activity').length;
    const modActive = students.filter((s) => s.activityClassification === 'Moderate').length;
    const needsImp  = students.filter(
      (s) => s.activityClassification === 'Low Activity' || s.activityClassification === 'Inactive'
    ).length;

    // ✅ Bucket by problemsSolved (assignment_submissions)
    // ❌ NOT: easy/medium/hard solved counts from LeetCode
    const SOLVED_BUCKETS = ["0–5", "5–10", "10–20", "20–40", "40+"];
    const solvedCounts   = { "0–5": 0, "5–10": 0, "10–20": 0, "20–40": 0, "40+": 0 };
    students.forEach((s) => {
      const t = s.problemsSolved ?? 0;   // ✅ assignment-based, safe fallback to 0
      if      (t < 5)  solvedCounts["0–5"]++;
      else if (t < 10) solvedCounts["5–10"]++;
      else if (t < 20) solvedCounts["10–20"]++;
      else if (t < 40) solvedCounts["20–40"]++;
      else             solvedCounts["40+"]++;
    });

    // ✅ Bucket by completionRate (assignment_submissions, 0–100)
    // ❌ NOT: contestRating distribution
    const COMPLETION_BUCKETS = ["0–25%", "25–50%", "50–75%", "75–100%"];
    const completionCounts   = { "0–25%": 0, "25–50%": 0, "50–75%": 0, "75–100%": 0 };
    students.forEach((s) => {
      const r = s.completionRate ?? 0;   // ✅ assignment-based, safe fallback to 0
      if      (r < 25) completionCounts["0–25%"]++;
      else if (r < 50) completionCounts["25–50%"]++;
      else if (r < 75) completionCounts["50–75%"]++;
      else             completionCounts["75–100%"]++;
    });

    return {
      total,
      avgSolved,
      avgCompletion,
      active,
      activityData: [
        { name: "High Activity", value: active    },
        { name: "Moderate",      value: modActive },
        { name: "Inactive/Low",  value: needsImp  },
      ],
      solvedData:     SOLVED_BUCKETS.map((name) => ({ name, value: solvedCounts[name] })),
      completionData: COMPLETION_BUCKETS.map((name) => ({ name, value: completionCounts[name] })),
    };
  }, [students]);

  // ✅ Summary cards — labels clarified to show assignment-only source
  const summaryCards = [
    {
      icon:  <PeopleAltRoundedIcon />,
      label: "Total Students",
      value: analytics.total,
      color: PRIMARY,
    },
    {
      icon:  <CodeRoundedIcon />,
      label: "Avg Assignment Problems Solved",  // ✅ from assignment_submissions
      value: analytics.avgSolved,
      color: SUCCESS,
    },
    {
      icon:  <EmojiEventsRoundedIcon />,
      label: "Avg Completion Rate",     // ✅ from assignment_submissions, NOT contestRating
      value: `${analytics.avgCompletion}%`,
      color: WARNING,
    },
    {
      icon:  <LocalFireDepartmentRoundedIcon />,
      label: "Active Coders",           // ✅ from activityClassification (assignment_submissions)
      value: analytics.active,
      color: DANGER,
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      {/* ── Source banner — makes scope explicit ── */}
      <Box
        sx={{
          display:      "flex",
          alignItems:   "center",
          gap:          1,
          mb:           2,
          px:           2,
          py:           1,
          borderRadius: 2,
          border:       "1px solid",
          borderColor:  "success.light",
          bgcolor:      (theme) =>
            theme.palette.mode === "dark" ? "rgba(47,158,68,0.08)" : "#f0fdf4",
        }}
      >
        <AssignmentTurnedInRoundedIcon sx={{ color: "success.main", fontSize: 18 }} />
        <Typography variant="body2" fontWeight={500} color="success.dark" sx={{ lineHeight: 1.55 }}>
          Assignment Analytics — all metrics below are based on assignment submissions only.
          LeetCode global solve counts are shown separately in LeetCode mode.
        </Typography>
      </Box>

      {/* ── Section Header ── */}
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{ ...gradientTextSx, mb: 2 }}
      >
        Batch Analytics
      </Typography>

      {/* ── SECTION 1 — Summary Cards ── */}
      <Grid container spacing={3.5} sx={{ mb: 3.5 }}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <SummaryCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* ── SECTION 2 — Charts ── */}
      <Grid container spacing={4} sx={{ mb: 3.5 }}>
        {/* Chart 1: Student Activity Status (from assignment_submissions) */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Student Activity Status">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={analytics.activityData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  label={({ value }) => value > 0 ? value : ""}
                  labelLine={false}
                >
                  {analytics.activityData.map((_, i) => (
                    <Cell key={i} fill={ACTIV_COLORS[i % ACTIV_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Chart 2: Completion Rate Distribution (from assignment_submissions) */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Completion Rate Distribution">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={analytics.completionData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} width={32} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Students" fill={INDIGO} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        {/* ── SECTION 3 — Problem Solving Distribution (from assignment_submissions) ── */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Assignment Problems Solved Distribution">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={analytics.solvedData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} width={32} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Students" fill={PRIMARY} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BatchAnalyticsDashboard;
